import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Button from './Button'
import theme from '../styles/theme'
import { searchUserByEmail, getUserBySessionMId, getUserByExternalId } from '../services/api'
import { useAuditLog, AUDIT_ACTIONS } from '../services/auditLog'
import UserDataCache from '../services/userDataCache'

const SearchContainer = styled.div`
  background: ${theme.color.background.frame};
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 8px 16px ${theme.color.shadow.medium}, 
              0 4px 8px ${theme.color.shadow.light};
  margin-bottom: 2rem;
`

const SearchForm = styled.form`
  display: flex;
  gap: 1rem;
  align-items: end;
  flex-wrap: wrap;
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 200px;
`

const Label = styled.label`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${theme.color.text.base};
`

const Input = styled.input`
  padding: 1rem;
  border: 2px solid ${theme.color.border.light};
  border-radius: 8px;
  font-size: 1.4rem;
  background: ${theme.color.background.light};
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.color.accent.primary};
    box-shadow: inset 0 2px 4px ${theme.color.shadow.light},
                0 0 0 3px rgba(215, 0, 54, 0.1);
  }
`

const Select = styled.select`
  padding: 1rem;
  border: 2px solid ${theme.color.border.light};
  border-radius: 8px;
  font-size: 1.4rem;
  background: ${theme.color.background.light};
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.color.accent.primary};
    box-shadow: inset 0 2px 4px ${theme.color.shadow.light},
                0 0 0 3px rgba(215, 0, 54, 0.1);
  }
`

const ErrorMessage = styled.div`
  background: #ffe6e6;
  color: #d70036;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`

const NotFoundContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`

const NotFoundMessage = styled.div`
  background: #fff3cd;
  color: #856404;
  padding: 1rem;
  border-radius: 4px;
  border: 1px solid #ffeaa7;
`

const Title = styled.h2`
  font-size: 2.4rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 2rem;
`

interface UserSearchProps {
  onUserFound: (userData: any) => void
  onCreateProfile?: (email: string) => void
  urlEmailParam?: string | null
  userData?: any
  onRefresh?: () => void
}

const UserSearch: React.FC<UserSearchProps> = ({ onUserFound, onCreateProfile, urlEmailParam, userData, onRefresh }) => {
  const { logEvent } = useAuditLog()
  const [searchType, setSearchType] = useState<'email' | 'external_id' | 'sessionm_id'>('email')
  const [searchValue, setSearchValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [showCreateProfile, setShowCreateProfile] = useState(false)

  // Handle URL email parameter
  useEffect(() => {
    if (urlEmailParam) {
      setSearchType('email')
      setSearchValue(urlEmailParam)
      // Automatically perform search
      performSearch('email', urlEmailParam)
    }
  }, [urlEmailParam])

  const performSearch = async (type: string, value: string) => {
    if (!value.trim()) return

    setIsLoading(true)
    setError('')
    setShowCreateProfile(false)

    // Clear cache for the user being searched to ensure fresh data
    const cache = UserDataCache.getInstance()
    if (type === 'email') {
      cache.clearUser(value.trim())
    }

    try {
      let userData

      if (type === 'email') {
        const searchResult = await searchUserByEmail(value)
        console.log('Search result:', searchResult)
        
        // Check if we got a user directly or in an array
        if (searchResult.user) {
          // Check if we have full profile data, if not get it separately
          if (searchResult.user.user_profile && searchResult.user.tier) {
            // We have full data, use it directly
            userData = searchResult
          } else {
            // Get full profile using SessionM ID
            userData = await getUserBySessionMId(searchResult.user.id)
          }
        } else if (searchResult.users && searchResult.users.length > 0) {
          // Users array - get full profile using SessionM ID
          userData = await getUserBySessionMId(searchResult.users[0].id)
        } else {
          throw new Error('User not found')
        }
      } else if (type === 'sessionm_id') {
        userData = await getUserBySessionMId(value)
      } else if (type === 'external_id') {
        userData = await getUserByExternalId(value)
      }

      // Log audit event for successful user search
      await logEvent({
        tabName: 'User Search',
        action: AUDIT_ACTIONS.USER_SEARCHED,
        details: {
          search_type: type,
          search_value: value,
          result_found: true,
          target_user_id: userData?.user?.id
        },
        targetUserId: userData?.user?.id,
        targetUserEmail: userData?.user?.email
      })

      onUserFound(userData)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred while searching'
      setError(errorMessage)
      
      // Show create profile option only for email searches when user not found
      if (type === 'email' && errorMessage.toLowerCase().includes('not found')) {
        setShowCreateProfile(true)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await performSearch(searchType, searchValue)
  }

  const handleCreateProfile = () => {
    if (onCreateProfile && searchType === 'email') {
      onCreateProfile(searchValue)
    }
  }

  return (
    <SearchContainer>
      <Title>User Search</Title>
      <SearchForm onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="searchType">Search Type</Label>
          <Select
            id="searchType"
            value={searchType}
            onChange={(e) => setSearchType(e.target.value as any)}
          >
            <option value="email">Email Address</option>
            <option value="external_id">External ID (Mariana Tek ID)</option>
            <option value="sessionm_id">SessionM ID</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="searchValue">
            {searchType === 'email' && 'Email Address'}
            {searchType === 'external_id' && 'External ID'}
            {searchType === 'sessionm_id' && 'SessionM ID'}
          </Label>
          <Input
            id="searchValue"
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder={
              searchType === 'email' ? 'user@example.com' :
              searchType === 'external_id' ? '123456' :
              '47a7a8f2-5f40-11ed-8591-f0d2ff101861'
            }
          />
        </FormGroup>

        <ButtonContainer>
          <Button type="submit" primary disabled={isLoading || !searchValue.trim()}>
            {isLoading ? 'Searching...' : 'Search User'}
          </Button>
          {userData && onRefresh && (
            <Button onClick={onRefresh} disabled={isLoading}>
              Refresh Profile
            </Button>
          )}
        </ButtonContainer>
      </SearchForm>

      {error && !showCreateProfile && <ErrorMessage>{error}</ErrorMessage>}
      
      {showCreateProfile && (
        <NotFoundContainer>
          <NotFoundMessage>
            User not found with email: {searchValue}
          </NotFoundMessage>
          <ButtonContainer>
            <Button onClick={handleCreateProfile} primary>
              Create a Profile
            </Button>
          </ButtonContainer>
        </NotFoundContainer>
      )}
    </SearchContainer>
  )
}

export default UserSearch